import { createContext, useCallback, useContext, useMemo, useRef, useState, type PropsWithChildren } from 'react'

export type TripDrawerPresentation = 'modal' | 'right-panel'

export interface TripDrawerPresentationContextValue {
  hasRightPanelPortal: boolean
  portalElement: HTMLElement | null
  setPortalElement: (element: HTMLElement | null) => void
  registerDrawer: (drawerId: symbol) => () => void
  isTopDrawer: (drawerId: symbol) => boolean
}

const TripDrawerPresentationContext = createContext<TripDrawerPresentationContextValue | undefined>(undefined)

export function TripDrawerPresentationProvider({
  children,
}: PropsWithChildren) {
  const [portalElement, setPortalElement] = useState<HTMLElement | null>(null)
  const drawerStackRef = useRef<Array<symbol>>([])

  const registerDrawer = useCallback((drawerId: symbol) => {
    drawerStackRef.current.push(drawerId)

    return () => {
      const index = drawerStackRef.current.lastIndexOf(drawerId)
      if (index >= 0) {
        drawerStackRef.current.splice(index, 1)
      }
    }
  }, [])

  const isTopDrawer = useCallback((drawerId: symbol) => {
    return drawerStackRef.current.at(-1) === drawerId
  }, [])

  const value = useMemo<TripDrawerPresentationContextValue>(() => ({
    hasRightPanelPortal: !!portalElement,
    portalElement,
    setPortalElement,
    registerDrawer,
    isTopDrawer,
  }), [isTopDrawer, portalElement, registerDrawer])

  return (
    <TripDrawerPresentationContext.Provider value={value}>
      {children}
    </TripDrawerPresentationContext.Provider>
  )
}

export function useTripDrawerPresentation() {
  const context = useContext(TripDrawerPresentationContext)
  if (!context) {
    throw new Error('useTripDrawerPresentation must be used within TripDrawerPresentationProvider')
  }
  return context
}

export function useRightPanelTripDrawerPresentation(): TripDrawerPresentation {
  const { hasRightPanelPortal } = useTripDrawerPresentation()
  return hasRightPanelPortal ? 'right-panel' : 'modal'
}

export function shouldRenderTripDrawerTitleInContent(
  presentation: TripDrawerPresentation,
): boolean {
  return presentation === 'modal'
}

export function useTripDrawerPortalTargetRef() {
  const { setPortalElement } = useTripDrawerPresentation()

  return useCallback((element: HTMLElement | null) => {
    setPortalElement(element)
  }, [setPortalElement])
}
